﻿import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { DateTimePicker, DateTimePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { Window } from '@progress/kendo-react-dialogs';
import { NumericTextBox, TextBox } from '@progress/kendo-react-inputs';
import { Popup, PopupProps } from '@progress/kendo-react-popup';
import { SVGIcon } from '@progress/kendo-svg-icons';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { DateTimeFormatter } from '../../../formatters';
import {
    AirplaneOutline,
    AirportTowerOutline,
    CheckmarkCircleSolid,
    ClockOutline,
    CogOutline,
    LocationPinOutline,
    WarningTriangleOutline
} from '../../../icons/icons';
import { FlightDataEntryService } from '../../../services/axios/axios-api-v2-generated';
import { LoadingIndicatorLinear } from '../../components/indicators/LoadingIndicatorLinear';
import { DropdownListWithValueField } from '../../components/inputs/DropdownListWithValueField';
import SegmentedControl from '../../components/inputs/SegmentedControl';
import { useEvent } from '../../contexts/event-bus';
import { useFormDropdownValue, useFormNumericValue, useFormRadioValue, useFormStringValue } from '../home/hooks/forms';
import './FlightDataEntryWindow.css';
import { FlightDataEntryRequestBase, FlightEntryWindowType, FlightPhase, RecoveryType } from './types';

function FieldLabel({ children }: { children: React.ReactNode }) {
    return (
        <span className="secondary-text-color" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {children}
        </span>
    );
}

function FormField({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
    return (
        <div className={`d-flex flex-column gap-1 ${className ?? ''}`}>
            <FieldLabel>{label}</FieldLabel>
            {children}
        </div>
    );
}

function SectionHeading({ icon, title }: { icon: SVGIcon; title: string }) {
    return (
        <div className="d-flex align-items-center gap-2 pb-2" style={{ borderBottom: '1px solid var(--border-color)' }}>
            <SvgIcon icon={icon} size="small" className="secondary-text-color" />
            <span className="k-font-weight-medium" style={{ fontSize: '0.85rem' }}>
                {title}
            </span>
        </div>
    );
}

function StatusFeedback({
    loading,
    success,
    successMessage,
    errorMessage
}: {
    loading: boolean;
    success: boolean;
    successMessage: string;
    errorMessage: string | null;
}) {
    const hasContent = loading || success || !!errorMessage;
    return (
        <div style={{ minHeight: 42 }}>
            {hasContent && (
                <div className="pt-2" style={{ borderTop: '1px solid var(--border-color)' }}>
                    {loading && <LoadingIndicatorLinear />}
                    {!loading && success && (
                        <div className="d-flex align-items-center gap-1 text-success" style={{ fontSize: '0.8rem' }}>
                            <SvgIcon icon={CheckmarkCircleSolid} size="small" />
                            <span>{successMessage}</span>
                        </div>
                    )}
                    {!loading && errorMessage && (
                        <div className="d-flex align-items-center gap-1 text-danger" style={{ fontSize: '0.8rem' }}>
                            <SvgIcon icon={WarningTriangleOutline} size="small" />
                            <span>{errorMessage}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Header({ flightInfo }: { flightInfo: FlightDataEntryRequestBase | null }) {
    const departureDateTime = flightInfo?.departureDateTime
        ? DateTimeFormatter.formatDateTime(dayjs(flightInfo.departureDateTime).toDate())
        : 'N/A';

    return (
        <div
            className="d-flex align-items-stretch overflow-hidden mx-3 mt-2 rounded"
            style={{ border: '1px solid var(--border-color)', fontSize: '0.82rem' }}>
            <div className="d-flex flex-column px-3 py-2 secondary-bg-color">
                <FieldLabel>ACID</FieldLabel>
                <span className="font-monospace fw-semibold mt-1">{flightInfo?.acid ?? 'N/A'}</span>
            </div>
            <div style={{ width: 1, background: 'var(--border-color)' }} />
            <div className="d-flex flex-column px-3 py-2">
                <FieldLabel>Origin</FieldLabel>
                <span className="fw-semibold mt-1">{flightInfo?.origin ?? 'N/A'}</span>
            </div>
            <div style={{ width: 1, background: 'var(--border-color)' }} />
            <div className="d-flex flex-column px-3 py-2 flex-grow-1">
                <FieldLabel>Departure</FieldLabel>
                <span className="font-monospace mt-1">{departureDateTime}</span>
            </div>
        </div>
    );
}

const windowDimensions: Record<FlightEntryWindowType, { width: number; height: number }> = {
    [FlightEntryWindowType.AcarsPosition]: { width: 460, height: 350 },
    [FlightEntryWindowType.AcarsSetPeriod]: { width: 460, height: 380 },
    [FlightEntryWindowType.ManualOOOITimes]: { width: 520, height: 400 },
    [FlightEntryWindowType.ManualDiversionRecoveryPriority]: { width: 460, height: 400 },
    [FlightEntryWindowType.ManualGateInfo]: { width: 460, height: 425 }
};

function FlightDataEntryWindow() {
    const [type, setType] = React.useState<FlightEntryWindowType | null>(null);
    const [flightInfo, setFlightInfo] = React.useState<FlightDataEntryRequestBase | null>(null);

    const windowTitle = useMemo(() => {
        switch (type) {
            case FlightEntryWindowType.AcarsPosition:
                return 'ACARS Position Request';
            case FlightEntryWindowType.AcarsSetPeriod:
                return 'ACARS Update Frequency';
            case FlightEntryWindowType.ManualOOOITimes:
                return 'Manual Aircraft Data Entry';
            case FlightEntryWindowType.ManualDiversionRecoveryPriority:
                return 'Manual Diversion Recovery Priority';
            case FlightEntryWindowType.ManualGateInfo:
                return 'Manual Gate Info';
            default:
                return 'Flight Data Entry';
        }
    }, [type]);

    useEvent(
        'show-flight-entry-window',
        React.useCallback(({ type, flightInfo }: { type: FlightEntryWindowType; flightInfo: FlightDataEntryRequestBase }) => {
            setType(type);
            setFlightInfo(flightInfo);
        }, [])
    );

    const dims = type ? windowDimensions[type] : { width: 480, height: 300 };

    return (
        <>
            {type !== null && (
                <Window
                    title={windowTitle}
                    width={dims.width}
                    height={dims.height}
                    modal={true}
                    resizable={false}
                    className="flight-data-entry-window"
                    onClose={() => setType(null)}>
                    <Header flightInfo={flightInfo} />
                    <div className="p-3 d-flex flex-column gap-3">
                        {type === FlightEntryWindowType.AcarsPosition && <AcarsPosition flightInfo={flightInfo} />}
                        {type === FlightEntryWindowType.AcarsSetPeriod && <AcarsPeriod flightInfo={flightInfo} />}
                        {type === FlightEntryWindowType.ManualOOOITimes && <ManualOOOITimes flightInfo={flightInfo} />}
                        {type === FlightEntryWindowType.ManualDiversionRecoveryPriority && (
                            <ManualDiversionRecovery flightInfo={flightInfo} />
                        )}
                        {type === FlightEntryWindowType.ManualGateInfo && <ManualGateUpdate flightInfo={flightInfo} />}
                    </div>
                </Window>
            )}
        </>
    );
}

function AcarsPosition({ flightInfo }: { flightInfo: FlightDataEntryRequestBase | null }) {
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [success, setSuccess] = React.useState(false);

    const requestPosition = async () => {
        setErrorMessage(null);
        setLoading(true);
        setSuccess(false);
        try {
            await FlightDataEntryService.requestAcarsPosition(
                {
                    body: {
                        aircraftId: flightInfo?.acid ?? '',
                        origin: flightInfo?.origin ?? '',
                        originalDepartureDateTime: flightInfo?.departureDateTime ?? ''
                    }
                },
                { withAuthorization: true }
            );
            setSuccess(true);
        } catch (error: any) {
            const status = error?.status || error?.response?.status;
            if (status === 403) {
                setErrorMessage('You do not have permission to request this ACARS position.');
            } else if (status === 500) {
                setErrorMessage('Server error occurred. Please try again later.');
            } else {
                setErrorMessage('Error requesting ACARS position. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="d-flex flex-column gap-3">
            <SectionHeading icon={LocationPinOutline} title="Request live position" />
            <p className="secondary-text-color m-0" style={{ fontSize: '0.85rem' }}>
                Sends an immediate position request to the aircraft via ACARS.
            </p>
            <Button themeColor="primary" onClick={requestPosition} disabled={loading}>
                Request Position
            </Button>
            <StatusFeedback
                loading={loading}
                success={success}
                successMessage="ACARS position request sent successfully."
                errorMessage={errorMessage}
            />
        </div>
    );
}

function AcarsPeriod({ flightInfo }: { flightInfo: FlightDataEntryRequestBase | null }) {
    const updateFrequency = useFormNumericValue(1);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [success, setSuccess] = React.useState(false);

    const requestPeriod = async () => {
        setErrorMessage(null);
        setLoading(true);
        setSuccess(false);
        try {
            await FlightDataEntryService.setAcarsPositionUpdateFrequency(
                {
                    body: {
                        aircraftId: flightInfo?.acid ?? '',
                        origin: flightInfo?.origin ?? '',
                        originalDepartureDateTime: flightInfo?.departureDateTime ?? '',
                        updateFrequencyMinutes: updateFrequency.value ?? undefined
                    }
                },
                { withAuthorization: true }
            );
            setSuccess(true);
        } catch (error: any) {
            const status = error?.status || error?.response?.status;
            if (status === 403) {
                setErrorMessage('You do not have permission to set the ACARS update frequency.');
            } else if (status === 500) {
                setErrorMessage('Server error occurred. Please try again later.');
            } else if (status === 400) {
                setErrorMessage('Invalid update frequency. Enter a value between 1 and 1440 minutes.');
            } else {
                setErrorMessage('Error setting update frequency. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="d-flex flex-column gap-3">
            <SectionHeading icon={CogOutline} title="Configure update interval" />
            <FormField label="Interval (minutes)">
                <NumericTextBox {...updateFrequency} min={1} max={1440} format={'n0'} width={120} />
            </FormField>
            <Button themeColor="primary" onClick={requestPeriod} disabled={loading}>
                Set Update Frequency
            </Button>
            <StatusFeedback
                loading={loading}
                success={success}
                successMessage="ACARS update frequency set successfully."
                errorMessage={errorMessage}
            />
        </div>
    );
}

const FlightPhaseOptions = [
    { value: FlightPhase.Out, text: 'Out' },
    { value: FlightPhase.Off, text: 'Off' },
    { value: FlightPhase.On, text: 'On' },
    { value: FlightPhase.In, text: 'In' }
];

function ManualOOOITimes({ flightInfo }: { flightInfo: FlightDataEntryRequestBase | null }) {
    const phase = useFormDropdownValue(FlightPhaseOptions, FlightPhase.Out);
    const [phaseTime, setPhaseTime] = React.useState<Date | null>(new Date());
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [success, setSuccess] = React.useState(false);

    const updatePhaseTime = (e: DateTimePickerChangeEvent) => setPhaseTime(DateTimeFormatter.parseTimeFromPicker(e.value, true));

    const submitEntry = async () => {
        setErrorMessage(null);
        setLoading(true);
        setSuccess(false);
        try {
            await FlightDataEntryService.submitManualThreeDpiEntry(
                {
                    body: {
                        aircraftId: flightInfo?.acid ?? '',
                        origin: flightInfo?.origin ?? '',
                        originalDepartureDateTime: flightInfo?.departureDateTime ?? '',
                        phase: mapPhase(phase.value),
                        phaseDateTime: phaseTime?.toISOString()
                    }
                },
                { withAuthorization: true }
            );
            setSuccess(true);
        } catch (error: any) {
            const status = error?.status || error?.response?.status;
            if (status === 403) {
                setErrorMessage('You do not have permission to submit this entry.');
            } else if (status === 500) {
                setErrorMessage('Server error occurred. Please try again later.');
            } else if (status === 400) {
                setErrorMessage('Invalid request. Ensure the phase and time are valid.');
            } else {
                setErrorMessage('Error submitting entry. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const mapPhase = (phase: FlightPhase) => {
        switch (phase) {
            case FlightPhase.Out:
                return 0;
            case FlightPhase.Off:
                return 1;
            case FlightPhase.On:
                return 2;
            case FlightPhase.In:
                return 3;
            default:
                return 0;
        }
    };

    return (
        <div className="d-flex flex-column gap-3">
            <SectionHeading icon={ClockOutline} title="Set OOOI phase time" />
            <div className="d-flex gap-3 align-items-end">
                <FormField label="Phase">
                    <DropdownListWithValueField {...phase} />
                </FormField>
                <FormField label="Time" className="flex-grow-1">
                    <DateTimePicker
                        popup={CustomCalendarPopup}
                        value={DateTimeFormatter.formatTimeForPicker(phaseTime)}
                        onChange={updatePhaseTime}
                        format={DateTimeFormatter.getKendoDateTimeFormat()}
                    />
                </FormField>
            </div>
            <Button themeColor="primary" onClick={submitEntry} disabled={loading}>
                Set Time
            </Button>
            <StatusFeedback loading={loading} success={success} successMessage="Phase time set successfully." errorMessage={errorMessage} />
        </div>
    );
}

const CustomCalendarPopup = (props: PopupProps) => {
    return (
        <Popup
            {...props}
            animate={{
                openDuration: 200,
                closeDuration: 200,
                type: 'slide',
                direction: 'right'
            }}
            anchorAlign={{
                horizontal: 'right',
                vertical: 'bottom'
            }}
            popupAlign={{
                horizontal: 'left',
                vertical: 'center'
            }}
        />
    );
};

const FaaPriorityOptions = [
    { value: 0, text: 'None' },
    { value: 8, text: 'Yes' },
    { value: 15, text: 'High' }
];

const MAX_PRIORITY = 16;

function ManualDiversionRecovery({ flightInfo }: { flightInfo: FlightDataEntryRequestBase | null }) {
    const faaCustom = useFormRadioValue(
        [
            { value: RecoveryType.Faa, label: 'FAA' },
            { value: RecoveryType.Custom, label: 'Custom' }
        ],
        RecoveryType.Faa
    );
    const customPriority = useFormDropdownValue(
        Array.from({ length: MAX_PRIORITY + 1 }, (_, i) => ({ value: i, text: i.toString() })),
        0
    );
    const faaPriority = useFormDropdownValue(FaaPriorityOptions, 0);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [success, setSuccess] = React.useState(false);

    const setPriority = async () => {
        setErrorMessage(null);
        setLoading(true);
        setSuccess(false);
        try {
            await FlightDataEntryService.setDiversionRecoveryPriority(
                {
                    body: {
                        aircraftId: flightInfo?.acid ?? '',
                        origin: flightInfo?.origin ?? '',
                        originalDepartureDateTime: flightInfo?.departureDateTime ?? '',
                        priority: faaCustom.value === RecoveryType.Custom ? (customPriority.value ?? 0) : (faaPriority.value ?? 0)
                    }
                },
                { withAuthorization: true }
            );
            setSuccess(true);
        } catch (error: any) {
            const status = error?.status || error?.response?.status;
            if (status === 403) {
                setErrorMessage('You do not have permission to set recovery priority.');
            } else if (status === 500) {
                setErrorMessage('Server error occurred. Please try again later.');
            } else if (status === 400) {
                setErrorMessage('Invalid request. Ensure the priority value is valid.');
            } else {
                setErrorMessage('Error setting priority. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="d-flex flex-column gap-3">
            <SectionHeading icon={AirplaneOutline} title="Set diversion recovery priority" />
            <div className="d-flex gap-3 align-items-end">
                <FormField label="Mode">
                    <SegmentedControl {...faaCustom} />
                </FormField>
                <FormField label="Priority">
                    {faaCustom.value === RecoveryType.Custom ? (
                        <DropdownListWithValueField {...customPriority} />
                    ) : (
                        <DropdownListWithValueField {...faaPriority} />
                    )}
                </FormField>
            </div>
            <Button themeColor="primary" onClick={setPriority} disabled={loading}>
                Set Priority
            </Button>
            <StatusFeedback
                loading={loading}
                success={success}
                successMessage="Diversion recovery priority updated."
                errorMessage={errorMessage}
            />
        </div>
    );
}

function ManualGateUpdate({ flightInfo }: { flightInfo: FlightDataEntryRequestBase | null }) {
    const departureGate = useFormStringValue('');
    const arrivalGate = useFormStringValue('');
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

    const handleApiError = (error: any) => {
        const status = error?.status || error?.response?.status;
        if (status === 403) {
            setErrorMessage('You do not have permission to update gate information.');
        } else if (status === 500) {
            setErrorMessage('Server error occurred. Please try again later.');
        } else if (status === 400) {
            setErrorMessage('Invalid gate information provided.');
        } else {
            setErrorMessage('Error updating gate info. Please try again.');
        }
    };

    const updateDepartureGate = async () => {
        setErrorMessage(null);
        setSuccessMessage(null);
        setLoading(true);
        try {
            await FlightDataEntryService.setDepartureGate(
                {
                    body: {
                        aircraftId: flightInfo?.acid ?? '',
                        origin: flightInfo?.origin ?? '',
                        originalDepartureDateTime: flightInfo?.departureDateTime ?? '',
                        gateNo: departureGate.value
                    }
                },
                { withAuthorization: true }
            );
            setSuccessMessage('Departure gate updated successfully.');
        } catch (error: any) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    const updateArrivalGate = async () => {
        setErrorMessage(null);
        setSuccessMessage(null);
        setLoading(true);
        try {
            await FlightDataEntryService.setArrivalGate(
                {
                    body: {
                        aircraftId: flightInfo?.acid ?? '',
                        origin: flightInfo?.origin ?? '',
                        originalDepartureDateTime: flightInfo?.departureDateTime ?? '',
                        gateNo: arrivalGate.value
                    }
                },
                { withAuthorization: true }
            );
            setSuccessMessage('Arrival gate updated successfully.');
        } catch (error: any) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="d-flex flex-column gap-3">
            <SectionHeading icon={AirportTowerOutline} title="Update gate assignments" />

            <div className="d-flex gap-2 align-items-end">
                <div className="d-flex flex-column gap-1 flex-grow-1">
                    <FieldLabel>Departure Gate</FieldLabel>
                    <TextBox {...departureGate} />
                </div>
                <Button themeColor="primary" onClick={updateDepartureGate} disabled={loading}>
                    Set
                </Button>
            </div>

            <div className="d-flex gap-2 align-items-end">
                <div className="d-flex flex-column gap-1 flex-grow-1">
                    <FieldLabel>Arrival Gate</FieldLabel>
                    <TextBox {...arrivalGate} />
                </div>
                <Button themeColor="primary" onClick={updateArrivalGate} disabled={loading}>
                    Set
                </Button>
            </div>

            <StatusFeedback
                loading={loading}
                success={!!successMessage}
                successMessage={successMessage ?? ''}
                errorMessage={errorMessage}
            />
        </div>
    );
}

export default FlightDataEntryWindow;
